import * as React from 'react'
import { Search, X } from 'lucide-react'
import { useDebounce } from 'use-debounce'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from './select'
import { cn } from '@/core/utils'

interface DropdownOption {
  value: string
  label: string
  disabled?: boolean
  icon?: React.ReactNode
  description?: React.ReactNode
}

interface DropdownProps<ValueType extends string> {
  options: Array<DropdownOption>
  value?: ValueType
  onValueChange?: (value: ValueType | null) => void
  placeholder?: string
  label?: string
  groupLabel?: string
  disabled?: boolean
  className?: string
  size?: 'sm' | 'default'
  error?: string
  required?: boolean
  searchable?: boolean
  emptyMessage?: string
  clearable?: boolean
  onSearch?: (query: string) => void
  isLoading?: boolean
  debounceDelay?: number
  filter?: (item: DropdownOption) => boolean
}

// Memoized option item component
const DropdownItem = React.memo(function DropdownItem({
  option,
}: {
  option: DropdownOption
}) {
  return (
    <SelectItem value={option.value} disabled={option.disabled}>
      <div className="flex items-center gap-2">
        {option.icon && <span className="shrink-0">{option.icon}</span>}
        <div className="flex flex-col">
          <span>{option.label}</span>
          {option.description && (
            <span className="text-xs text-muted-foreground">
              {option.description}
            </span>
          )}
        </div>
      </div>
    </SelectItem>
  )
})

// Memoized search input component
const SearchInput = React.memo(
  React.forwardRef<
    HTMLInputElement,
    {
      value: string
      onChange: (value: string) => void
    }
  >(function SearchInput({ value, onChange }, ref) {
    const handleChange = React.useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        onChange(e.target.value)
      },
      [onChange],
    )

    return (
      <div className="flex items-center border-b px-3 pb-2">
        <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
        <input
          ref={ref}
          className="flex h-8 w-full bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50"
          placeholder="Search..."
          value={value}
          onChange={handleChange}
          onKeyDown={(e) => e.stopPropagation()}
          onFocus={(e) => e.stopPropagation()}
        />
      </div>
    )
  }),
)

// Memoized empty state component
const EmptyState = React.memo(function EmptyState({
  message,
}: {
  message: string
}) {
  return (
    <div className="py-6 text-center text-sm text-muted-foreground">
      {message}
    </div>
  )
})

// Memoized clear button component
const ClearButton = React.memo(function ClearButton({
  onClick,
}: {
  onClick: (e: React.MouseEvent) => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="absolute right-8 top-1/2 -translate-y-1/2 rounded-sm opacity-70 hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none"
      aria-label="Clear selection"
    >
      <X className="h-4 w-4" />
    </button>
  )
})

const Dropdown = <ValueType extends string>({
  options,
  value,
  onValueChange,
  placeholder = 'Select an option',
  label,
  groupLabel,
  disabled = false,
  className,
  size = 'default',
  error,
  required = false,
  searchable = false,
  emptyMessage = 'No options found',
  clearable = false,
  onSearch,
  isLoading = false,
  debounceDelay = 300,
  filter,
}: DropdownProps<ValueType>) => {
  const [searchQuery, setSearchQuery] = React.useState('')

  const inputRef = React.useRef<HTMLInputElement>(null)

  // Filter out empty value options as Radix Select doesn't allow empty strings
  const validOptions = React.useMemo(
    () => options.filter((opt) => opt.value !== ''),
    [options],
  )
  const [debouncedFormValues] = useDebounce(searchQuery, debounceDelay)
  // Filter options based on search query
  // If onSearch is provided, we assume the parent handles filtering
  const filteredOptions = React.useMemo(() => {
    if (!onSearch && (!searchable || !debouncedFormValues.trim()))
      return validOptions
    if (onSearch) return validOptions // Server-side filtering returns already filtered options
    if (filter) {
      return validOptions.filter(filter)
    }
    const query = debouncedFormValues.toLowerCase()
    if (!query) return validOptions
    return validOptions.filter(
      (opt) =>
        opt.label.toLowerCase().includes(query) ||
        opt.value.toLowerCase().includes(query),
    )
  }, [onSearch, searchable, debouncedFormValues, validOptions, filter])

  const handleClear = React.useCallback(
    (e: React.MouseEvent) => {
      onValueChange?.('' as ValueType)
      e.preventDefault()
    },
    [onValueChange],
  )

  const setFocus = React.useCallback(() => {
    if (searchable) {
      // Only auto-focus on desktop to avoid virtual keyboard issues on mobile
      const isDesktop = window.matchMedia('(min-width: 768px)').matches
      if (!isDesktop) return

      const timeoutDispatch = setTimeout(() => {
        inputRef.current?.focus({})
      }, 500)
      return () => clearTimeout(timeoutDispatch)
    }
  }, [searchable])

  const handleOpenChange = React.useCallback(
    (open: boolean) => {
      if (!open) {
        setSearchQuery('')
        onSearch?.('')
      } else {
        setFocus()
      }
    },
    [onSearch, setFocus],
  )

  const handleSearchChange = React.useCallback((newValue: string) => {
    setSearchQuery(newValue)
  }, [])

  React.useEffect(() => {
    onSearch?.(debouncedFormValues)
  }, [debouncedFormValues, onSearch])

  // Memoize trigger className
  const triggerClassName = React.useMemo(
    () =>
      cn(
        className,
        error && 'border-destructive focus-visible:ring-destructive/20',
      ),
    [className, error],
  )

  // Memoize label className
  const labelClassName = React.useMemo(
    () =>
      cn(
        'text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70',
        error && 'text-destructive',
      ),
    [error],
  )

  // Render options list
  const renderOptions = React.useMemo(() => {
    if (filteredOptions.length === 0) {
      return <EmptyState message={emptyMessage} />
    }

    const items = filteredOptions.map((option) => (
      <DropdownItem key={option.value} option={option} />
    ))

    if (groupLabel) {
      return (
        <SelectGroup>
          <SelectLabel>{groupLabel}</SelectLabel>
          {items}
        </SelectGroup>
      )
    }
    return items
  }, [filteredOptions, groupLabel, emptyMessage])
  return (
    <div className="flex flex-col gap-2 w-full">
      {label && (
        <label className={labelClassName}>
          {label}
          {required && <span className="text-destructive ml-1">*</span>}
        </label>
      )}
      <div className="relative">
        <Select<ValueType>
          value={value}
          onValueChange={onValueChange}
          disabled={disabled}
          onOpenChange={handleOpenChange}
          items={filteredOptions}
        >
          <SelectTrigger
            className={triggerClassName}
            size={size}
            aria-invalid={!!error}
            type="button"
            onClick={(e) => e.preventDefault()}
          >
            <SelectValue placeholder={placeholder} />
          </SelectTrigger>
          <SelectContent>
            {searchable && (
              <SearchInput
                ref={inputRef}
                value={searchQuery}
                onChange={handleSearchChange}
              />
            )}
            {isLoading && (
              <div className="py-6 text-center text-sm text-muted-foreground">
                Loading...
              </div>
            )}
            <div className="overflow-scroll max-h-50">{renderOptions}</div>
          </SelectContent>
        </Select>

        {clearable && value && !disabled && (
          <ClearButton onClick={handleClear} />
        )}
      </div>
      {error && (
        <p className="text-sm text-destructive" role="alert">
          {error}
        </p>
      )}
    </div>
  )
}
Dropdown.displayName = 'Dropdown'

export { Dropdown }
export type { DropdownOption, DropdownProps }
